import { useState } from 'react';
import { ArrowLeft, Pill, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { healthDataService, MedicationEntryDto } from '../services/health-data.service';
import { LoadingSkeleton } from './LoadingSkeleton';
import toast from 'react-hot-toast';
import { handleApiError } from '../lib/api-client';

interface MedicationTrackerProps {
  onBack: () => void;
}

export function MedicationTracker({ onBack }: MedicationTrackerProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [dosage, setDosage] = useState('');
  const [unit, setUnit] = useState('mg');
  const [type, setType] = useState('');
  const [notes, setNotes] = useState('');
  const [showForm, setShowForm] = useState(false);

  const units = ['mg', 'ml', 'tablet', 'capsule', 'unit', 'g'];
  const types = ['oral', 'injection', 'topical', 'inhalation', 'other'];

  // Fetch medication entries
  const {
    data: entriesData,
    isLoading: entriesLoading,
  } = useQuery({
    queryKey: ['medications', user?.id],
    queryFn: () => healthDataService.getMedicationEntries({ limit: 50 }),
    enabled: !!user,
  });

  const entries = entriesData?.data || entriesData || [];

  // Create medication entry mutation
  const createMutation = useMutation({
    mutationFn: (data: MedicationEntryDto) => healthDataService.createMedicationEntry(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['medications'] });
      toast.success('Medication logged successfully!');
      setName('');
      setDosage('');
      setUnit('mg');
      setType('');
      setNotes('');
      setShowForm(false);
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !dosage || isNaN(Number(dosage))) {
      toast.error('Please enter medication name and valid dosage');
      return;
    }

    createMutation.mutate({
      name: name.trim(),
      dosage: Number(dosage),
      unit,
      type: type || undefined,
      notes: notes.trim() || undefined,
    });
  };

  if (entriesLoading) {
    return <LoadingSkeleton />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
      <div className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={onBack}
              className="flex items-center space-x-2 text-gray-700 hover:text-dropsugar-green transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Back</span>
            </button>
            <h1 className="text-xl font-bold text-gray-900">Medication Tracking</h1>
            <div className="w-20" />
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8">
        {!showForm ? (
          <button
            onClick={() => setShowForm(true)}
            className="w-full bg-gradient-to-r from-dropsugar-blue to-dropsugar-blue-dark text-white py-4 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all mb-8"
          >
            + Add Medication
          </button>
        ) : (
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
            <h2 className="text-xl font-bold mb-4">New Medication Entry</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Medication Name *
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., Metformin"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dropsugar-green focus:border-transparent"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Dosage *
                  </label>
                  <input
                    type="number"
                    value={dosage}
                    onChange={(e) => setDosage(e.target.value)}
                    placeholder="Enter dosage"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dropsugar-green focus:border-transparent"
                    required
                    min="0"
                    step="0.01"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Unit *
                  </label>
                  <select
                    value={unit}
                    onChange={(e) => setUnit(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dropsugar-green focus:border-transparent"
                  >
                    {units.map((u) => (
                      <option key={u} value={u}>
                        {u}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Type (Optional)
                </label>
                <select
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dropsugar-green focus:border-transparent"
                >
                  <option value="">Select type</option>
                  {types.map((t) => (
                    <option key={t} value={t}>
                      {t.charAt(0).toUpperCase() + t.slice(1)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes (Optional)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add any notes..."
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dropsugar-green focus:border-transparent"
                />
              </div>

              <div className="flex space-x-3">
                <button
                  type="submit"
                  disabled={createMutation.isPending}
                  className="flex-1 bg-dropsugar-green hover:bg-dropsugar-green-dark text-white py-2 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {createMutation.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save Medication'
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setName('');
                    setDosage('');
                    setUnit('mg');
                    setType('');
                    setNotes('');
                  }}
                  className="px-6 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-semibold transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h2 className="text-xl font-bold mb-4 flex items-center">
            <Pill className="w-6 h-6 mr-2 text-dropsugar-green" />
            Recent Medications
          </h2>
          {entries.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No medications logged yet. Add your first medication above!</p>
          ) : (
            <div className="space-y-3">
              {entries.map((entry: any) => {
                const date = new Date(entry.takenAt || entry.createdAt);
                return (
                  <div key={entry.id} className="p-4 rounded-lg border border-gray-200 bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          <span className="text-xl font-bold text-gray-900">{entry.name}</span>
                          <span className="text-lg text-gray-600">
                            {entry.dosage} {entry.unit}
                          </span>
                        </div>
                        {entry.type && (
                          <span className="inline-block mt-1 px-2 py-1 bg-dropsugar-green/20 text-dropsugar-green-dark rounded text-xs font-semibold">
                            {entry.type}
                          </span>
                        )}
                        <p className="text-sm text-gray-600 mt-1">
                          {date.toLocaleDateString()} {date.toLocaleTimeString()}
                        </p>
                        {entry.notes && (
                          <p className="text-sm text-gray-500 mt-1">{entry.notes}</p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
